import type { BenefitVerification } from '@/types/benefitsVerification';

/**
 * Creates mock BenefitVerification data for the tutorial
 * @param index - The index of the mock BV to create (0-9)
 * @returns A mock BenefitVerification object
 */
export const createMockBV = (index: number): BenefitVerification => {
	// Stable IDs for tutorial (table identifies rows by id for menu override)
	const FIRST_ROW_ID = '3825bf48-2dd1-f011-bbd2-7c1e526496b7';
	const SECOND_ROW_ID = '3f78728b-a95d-f011-bec1-7c1e526496b7';

	// First row: In Process status
	if (index === 0) {
		const baseId = FIRST_ROW_ID;
		return {
			ta_benefitsverificationid: baseId,
			ta_proceduredate: '2025-12-03',
			ta_bv_status: 953870000, // In Process status code
			bv_product_status: 'In Process',
			ta_portalbvupload: '',
			createdon: '2025-12-04T16:21:29Z',
			account: {
				accountid: '61aea445-e3b4-ea11-a816-000d3a191041',
				name: 'A Podiatry Clinic',
				statuscode: 1,
				emailaddress1: 'wakhtar@axisdatalytics.com',
				ta_originationsource: 0,
				modifiedon: '2025-11-06T20:31:49Z',
				createdon: '2020-06-22T23:51:36Z',
				ta_qbocustomerid: '',
				ta_accountstatus: 953870001,
			},
			provider: {
				fullname: 'Edward Jenner',
			},
			patient: {
				contactid: '94955697-8ad0-f011-bbd2-7c1e526496b7',
				fullname: 'Andrew Simon',
				primaryInsurance: {
					ta_insuranceid: 'e6b93b00-63d0-f011-bbd3-6045bdda2414',
					ta_name: 'Insurance B',
				},
				secondaryInsurance: {
					ta_insuranceid: '',
					ta_name: '',
				},
			},
			priorAuths: [
				{
					ta_priorauthid: '6125bf48-2dd1-f011-bbd2-7c1e526496b7',
					_ta_bv_value: baseId,
					ta_pa_status: 953870003, // In Process status code
					ta_name: 'Microlyte (A2005)',
				},
				{
					ta_priorauthid: '6425bf48-2dd1-f011-bbd2-7c1e526496b7',
					_ta_bv_value: baseId,
					ta_pa_status: 953870003, // In Process status code
					ta_name: 'Helicoll (Q4164)',
				},
			],
		};
	}

	// Second row: Approved status (IVR + Order Now; tutorial uses this row for both)
	if (index === 1) {
		const approvedId = SECOND_ROW_ID;
		return {
			ta_benefitsverificationid: approvedId,
			ta_proceduredate: '2025-07-24',
			ta_bv_status: 953870025, // Approved status code
			bv_product_status: 'Approved', // Order Now (and IVR when applicable); tutorial uses this row for both
			ta_portalbvupload: '',
			createdon: '2025-07-10T16:18:42Z',
			account: {
				accountid: '61aea445-e3b4-ea11-a816-000d3a191041',
				name: 'A Podiatry Clinic',
				statuscode: 1,
				emailaddress1: 'wakhtar@axisdatalytics.com',
				ta_originationsource: 0,
				modifiedon: '2025-11-06T20:31:49Z',
				createdon: '2020-06-22T23:51:36Z',
				ta_qbocustomerid: '',
				ta_accountstatus: 953870001,
			},
			provider: {
				fullname: 'Edward Jenner',
			},
			patient: {
				contactid: '62ea8202-4f73-ee11-9ae7-000d3a4e17dd',
				fullname: 'William Reed',
				primaryInsurance: {
					ta_insuranceid: '894af68f-7bc1-ea11-a812-000d3a1a1381',
					ta_name: '1199 National Benefit Fund (SNATB/13162)',
				},
				secondaryInsurance: {
					ta_insuranceid: '894af68f-7bc1-ea11-a812-000d3a1a1381',
					ta_name: '1199 National Benefit Fund (SNATB/13162)',
				},
			},
			priorAuths: [
				{
					ta_priorauthid: '24c7105a-ab5d-f011-bec1-7c1e520b3e6f',
					_ta_bv_value: approvedId,
					ta_pa_status: 953870001, // Approved status code
					ta_name: 'Axolotl Dual Graft (Q4332)',
				},
			],
		};
	}

	const uniqueId = crypto.randomUUID();

	// Other rows: In Process status (same as first row but with unique IDs)
	return {
		ta_benefitsverificationid: uniqueId,
		ta_proceduredate: '2025-12-03',
		ta_bv_status: 953870000, // In Process status code
		bv_product_status: 'In Process',
		ta_portalbvupload: '',
		createdon: '2025-12-04T16:21:29Z',
		account: {
			accountid: '61aea445-e3b4-ea11-a816-000d3a191041',
			name: 'A Podiatry Clinic',
			statuscode: 1,
			emailaddress1: 'wakhtar@axisdatalytics.com',
			ta_originationsource: 0,
			modifiedon: '2025-11-06T20:31:49Z',
			createdon: '2020-06-22T23:51:36Z',
			ta_qbocustomerid: '',
			ta_accountstatus: 953870001,
		},
		provider: {
			fullname: 'Edward Jenner',
		},
		patient: {
			contactid: '94955697-8ad0-f011-bbd2-7c1e526496b7',
			fullname: 'Andrew Simon',
			primaryInsurance: {
				ta_insuranceid: 'e6b93b00-63d0-f011-bbd3-6045bdda2414',
				ta_name: 'Insurance B',
			},
			secondaryInsurance: {
				ta_insuranceid: '',
				ta_name: '',
			},
		},
		priorAuths: [
			{
				ta_priorauthid: '6125bf48-2dd1-f011-bbd2-7c1e526496b7',
				_ta_bv_value: uniqueId,
				ta_pa_status: 953870003, // In Process status code
				ta_name: 'Microlyte (A2005)',
			},
			{
				ta_priorauthid: '6425bf48-2dd1-f011-bbd2-7c1e526496b7',
				_ta_bv_value: uniqueId,
				ta_pa_status: 953870003, // In Process status code
				ta_name: 'Helicoll (Q4164)',
			},
		],
	};
};

/**
 * Generates an array of mock BenefitVerification data for the tutorial
 * @param count - Number of mock BVs to generate (default: 10)
 * @returns Array of mock BenefitVerification objects
 */
export const generateMockBVData = (count = 10): BenefitVerification[] => {
	return Array.from({ length: count }, (_, index) => createMockBV(index));
};

